import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { userAPI, departmentAPI } from '../../api/services';

const AdminUserModal = React.lazy(() => import('./AdminUserModal'));
const AdminDepartmentModal = React.lazy(() => import('./AdminDepartmentModal'));
const LevelManagement = React.lazy(() => import('./LevelManagement'));
const RoleManagement = React.lazy(() => import('./RoleManagement'));
const PermissionMatrix = React.lazy(() => import('./PermissionMatrix'));
const WorkloadAnalysis = React.lazy(() => import('./WorkloadAnalysis'));
const ProjectBoard = React.lazy(() => import('../Common/ProjectBoard'));

const cap = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : '';
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

type PeopleTab = 'users' | 'departments' | 'workload' | 'board' | 'levels' | 'roles' | 'permissions';

const TABS: { key: PeopleTab; label: string }[] = [
  { key: 'users', label: 'Users' },
  { key: 'departments', label: 'Departments' },
  { key: 'workload', label: 'Workload' },
  { key: 'board', label: 'Board' },
  { key: 'levels', label: 'Levels' },
  { key: 'roles', label: 'Roles' },
  { key: 'permissions', label: 'Permissions' },
];

export default function AdminPeopleHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as PeopleTab | null;
  const [activeTab, setActiveTab] = useState<PeopleTab>(tabParam && TABS.some(t => t.key === tabParam) ? tabParam : 'users');

  const handleTabChange = (tab: PeopleTab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <h1 className="text-xl font-semibold text-gray-900">People</h1>
        <p className="text-sm text-gray-400 mt-0.5">Users, departments, levels, roles and permissions</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 px-6">
        <div className="flex gap-0 -mb-px">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <React.Suspense fallback={
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      }>
        {activeTab === 'users' && <UsersView />}
        {activeTab === 'departments' && <DepartmentsView />}
        {activeTab === 'workload' && <WorkloadAnalysis />}
        {activeTab === 'board' && <ProjectBoard />}
        {activeTab === 'levels' && <LevelManagement />}
        {activeTab === 'roles' && <RoleManagement />}
        {activeTab === 'permissions' && <PermissionMatrix />}
      </React.Suspense>
    </div>
  );
}

// ─── Users View ──────────────────────────────────────────────────────────────
function UsersView() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const perPage = 20;

  const fetchUsers = useCallback(async () => {
    try {
      const res = await userAPI.getAllUsers();
      setUsers(res.data?.data || res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filtered = useMemo(() => users.filter(u =>
    !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  ), [users, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);
  useEffect(() => { setPage(1); }, [search]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div>
      <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="w-64">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users..."
            className="bg-gray-100 rounded-lg px-3 py-1.5 text-sm w-full outline-none focus:ring-1 focus:ring-gray-300" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{filtered.length} users</span>
          <button onClick={() => setShowCreate(true)}
            className="text-xs text-gray-400 hover:text-blue-600 transition-colors">
            + Add User
          </button>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-1 px-6 py-2 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
        <div className="col-span-4">Name</div>
        <div className="col-span-3">Email</div>
        <div className="col-span-1">Level</div>
        <div className="col-span-2">Role</div>
        <div className="col-span-2 text-right">Status</div>
      </div>
      {paginated.map((u: any) => (
        <div key={u._id} onClick={() => setSelectedUserId(u._id)}
          className="grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50/80 transition-colors">
          <div className="col-span-4 flex items-center gap-2.5 min-w-0">
            <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center shrink-0">{(u.name || '?')[0].toUpperCase()}</div>
            <span className="text-sm text-gray-700 truncate capitalize">{u.name}</span>
          </div>
          <div className="col-span-3 flex items-center text-xs text-gray-500 truncate">{u.email}</div>
          <div className="col-span-1 flex items-center text-xs text-gray-500">{u.level || '—'}</div>
          <div className="col-span-2 flex items-center text-xs text-gray-400">{cap(u.role || '')}</div>
          <div className="col-span-2 flex items-center justify-end">
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${u.isActive !== false ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-400'}`}>
              {u.isActive !== false ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      ))}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-3">
          <span className="text-xs text-gray-400">{(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">Prev</button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}

      <React.Suspense fallback={null}>
        {(selectedUserId || showCreate) && (
          <AdminUserModal
            userId={selectedUserId}
            onClose={() => { setSelectedUserId(null); setShowCreate(false); }}
            onRefresh={fetchUsers}
          />
        )}
      </React.Suspense>
    </div>
  );
}

// ─── Departments View ────────────────────────────────────────────────────────
function DepartmentsView() {
  const [departments, setDepartments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null);

  const fetchDepts = useCallback(async () => {
    try {
      const res = await departmentAPI.getAllDepartments({ includeMembers: true });
      setDepartments(res.data?.data || res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDepts(); }, [fetchDepts]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div>
      <div className="grid grid-cols-12 gap-1 px-6 py-2 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
        <div className="col-span-4">Department</div>
        <div className="col-span-2">Head</div>
        <div className="col-span-2 text-right">Members</div>
        <div className="col-span-2 text-right">Status</div>
        <div className="col-span-2 text-right">Created</div>
      </div>
      {departments.length === 0 ? (
        <p className="text-center py-8 text-xs text-gray-400">No departments found</p>
      ) : departments.map((d: any) => (
        <div key={d._id} onClick={() => setSelectedDeptId(d._id)}
          className="grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50/80 transition-colors">
          <div className="col-span-4 flex items-center gap-2.5 min-w-0">
            <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21" /></svg>
            <span className="text-sm text-gray-700 truncate capitalize">{d.name?.toLowerCase()}</span>
          </div>
          <div className="col-span-2 flex items-center text-xs text-gray-500 truncate">{d.headOfDepartment?.name || '—'}</div>
          <div className="col-span-2 flex items-center justify-end text-xs text-gray-500 tabular-nums">{d.memberCount ?? d.members?.length ?? 0}</div>
          <div className="col-span-2 flex items-center justify-end">
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${d.isActive !== false ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-400'}`}>
              {d.isActive !== false ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="col-span-2 flex items-center justify-end text-xs text-gray-400">{fmtDate(d.createdAt)}</div>
        </div>
      ))}

      <React.Suspense fallback={null}>
        {selectedDeptId && (
          <AdminDepartmentModal
            departmentId={selectedDeptId}
            onClose={() => setSelectedDeptId(null)}
            onRefresh={fetchDepts}
          />
        )}
      </React.Suspense>
    </div>
  );
}
